import { X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { h3, p, sh3, textStyle } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import Card from '../ui/card';
import PrimaryButton from '../ui/PrimaryButton';
import './HyperLocalReferralTracker.css';
import { createInfluencer, updateInfluencer } from './referralTrackerApi';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

const getInitialForm = (influencer) => {
  if (!influencer) {
    return {
      site_id: '',
      name: '',
      email: '',
      phone: '',
      instagram: '',
      youtube: '',
      facebook: '',
      status: 'active',
    };
  }
  const sh = influencer.social_handles || {};
  return {
    site_id: influencer.site_id || '',
    name: influencer.name || '',
    email: influencer.email || '',
    phone: influencer.phone || '',
    instagram: sh.instagram || '',
    youtube: sh.youtube || '',
    facebook: sh.facebook || '',
    status: influencer.status || 'active',
  };
};

const NewReferralOverlay = ({ onClose, onSuccess, influencer: initialInfluencer }) => {
  const isEditMode = Boolean(initialInfluencer);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [form, setForm] = useState(() => getInitialForm(initialInfluencer));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(getInitialForm(initialInfluencer));
  }, [initialInfluencer]);

  const loadSites = useCallback(async () => {
    setSitesLoading(true);
    try {
      const data = await getAllSites();
      const list = Array.isArray(data) ? data : data?.sites ?? [];
      setSites(list);
    } catch (err) {
      console.error('Error loading sites:', err);
      setError('Failed to load sites.');
    } finally {
      setSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.site_id?.trim()) {
      setError('Please select a site.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        site_id: form.site_id.trim(),
        status: form.status || 'active',
      };
      if (form.name?.trim()) payload.name = form.name.trim();
      if (form.email?.trim()) payload.email = form.email.trim();
      if (form.phone?.trim()) payload.phone = form.phone.trim();

      const social_handles = {};
      if (form.instagram?.trim()) social_handles.instagram = form.instagram.trim();
      if (form.youtube?.trim()) social_handles.youtube = form.youtube.trim();
      if (form.facebook?.trim()) social_handles.facebook = form.facebook.trim();
      if (Object.keys(social_handles).length > 0) payload.social_handles = social_handles;

      if (isEditMode) {
        const influencerId = initialInfluencer.influencer_id || initialInfluencer.id;
        await updateInfluencer(influencerId, payload);
      } else {
        await createInfluencer(payload);
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || (isEditMode ? 'Failed to update influencer.' : 'Failed to create influencer.'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500';
  const labelClass = textStyle(sh3, 'text-gray-600 block mb-1');

  return (
    <div className="referral-tracker-details">
      <div className="referral-tracker-details-header">
        <h2 className={textStyle(h3, 'referral-tracker-details-title')}>
          {isEditMode ? 'Edit Influencer' : 'Create Influencer'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="referral-tracker-details-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <Card variant="light" className="referral-tracker-details-card p-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className={textStyle(p, 'text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2')}
              role="alert"
            >
              {error}
            </div>
          )}

          <div>
            <label htmlFor="new-referral-site_id" className={labelClass}>
              Select site <span className="text-red-500">*</span>
            </label>
            <select
              id="new-referral-site_id"
              value={form.site_id}
              onChange={(e) => handleChange('site_id', e.target.value)}
              className={inputClass}
              required
              disabled={sitesLoading}
            >
              <option value="">
                {sitesLoading ? 'Loading sites...' : 'Choose a site'}
              </option>
              {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
            </select>
          </div>

          <div>
            <label htmlFor="new-referral-name" className={labelClass}>
              Name
            </label>
            <input
              id="new-referral-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={inputClass}
              placeholder="Influencer name"
            />
          </div>

          <div>
            <label htmlFor="new-referral-email" className={labelClass}>
              Email
            </label>
            <input
              id="new-referral-email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={inputClass}
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label htmlFor="new-referral-phone" className={labelClass}>
              Phone
            </label>
            <input
              id="new-referral-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className={inputClass}
              placeholder="+91 9876543210"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="new-referral-instagram" className={labelClass}>
                Instagram
              </label>
              <input
                id="new-referral-instagram"
                type="text"
                value={form.instagram}
                onChange={(e) => handleChange('instagram', e.target.value)}
                className={inputClass}
                placeholder="@handle"
              />
            </div>
            <div>
              <label htmlFor="new-referral-youtube" className={labelClass}>
                YouTube
              </label>
              <input
                id="new-referral-youtube"
                type="url"
                value={form.youtube}
                onChange={(e) => handleChange('youtube', e.target.value)}
                className={inputClass}
                placeholder="https://youtube.com/..."
              />
            </div>
            <div>
              <label htmlFor="new-referral-facebook" className={labelClass}>
                Facebook
              </label>
              <input
                id="new-referral-facebook"
                type="text"
                value={form.facebook}
                onChange={(e) => handleChange('facebook', e.target.value)}
                className={inputClass}
                placeholder="Profile or page URL"
              />
            </div>
          </div>

          <div>
            <label htmlFor="new-referral-status" className={labelClass}>
              Status
            </label>
            <select
              id="new-referral-status"
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className={inputClass}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex align-center justify-center">
            <PrimaryButton
              type="submit"
              disabled={submitting}
              className="w-auto h-auto px-6 py-3 text-base"
            >
              {submitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Update Influencer' : 'Create Influencer')}
            </PrimaryButton>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default NewReferralOverlay;
